import { useMemo } from "react";
import type { MazeCellProps } from "./types";

/**
 * MazeCell Component
 *
 * A single cell in the maze grid. Renders different visuals based on
 * cell type (empty, obstacle, start, goal, collectible) with Pixel's
 * warm tech-toy aesthetic.
 */
export function MazeCell({
  type,
  position,
  isPathPreview = false,
  isCurrentStep = false,
  collectible,
  onTap,
  disabled = false,
}: MazeCellProps) {
  // Cell appearance based on type
  const cellStyles = useMemo(() => {
    const base = `
      relative
      w-full aspect-square
      rounded-xl
      transition-all duration-200 ease-bounce
      flex items-center justify-center
      border-2
    `;

    switch (type) {
      case "start":
        return `${base}
          bg-gradient-to-br from-pixel-light via-pixel/80 to-pixel
          border-pixel-dark
          shadow-[inset_0_2px_8px_rgba(255,140,66,0.3),0_2px_0_0_theme(colors.pixel.dark)]
        `;

      case "goal":
        return `${base}
          bg-gradient-to-br from-pixel-accent/30 via-pixel-accent/40 to-pixel-accent/50
          border-pixel-accent/60
          shadow-[inset_0_0_20px_rgba(0,212,255,0.3),0_2px_0_0_rgba(0,180,216,0.5)]
          animate-pulse
        `;

      case "obstacle":
        return `${base}
          bg-gradient-to-br from-neutral-300 via-neutral-400 to-neutral-500
          border-neutral-500
          shadow-[inset_0_-2px_4px_rgba(0,0,0,0.2),0_2px_0_0_theme(colors.neutral.600)]
        `;

      case "collectible":
        return `${base}
          bg-gradient-to-br from-white via-neutral-50 to-neutral-100
          border-neutral-200
          shadow-[0_2px_0_0_theme(colors.neutral.300),0_4px_12px_rgba(0,0,0,0.06)]
          ${!disabled ? "hover:border-pixel/40 hover:shadow-[0_4px_0_0_theme(colors.pixel.light),0_6px_16px_rgba(255,140,66,0.15)]" : ""}
        `;

      case "empty":
      default:
        return `${base}
          bg-gradient-to-br from-white via-neutral-50 to-neutral-100
          border-neutral-200
          shadow-[0_2px_0_0_theme(colors.neutral.300),0_4px_12px_rgba(0,0,0,0.06)]
          ${!disabled && onTap ? "hover:border-pixel/40 hover:shadow-[0_4px_0_0_theme(colors.pixel.light),0_6px_16px_rgba(255,140,66,0.15)] cursor-pointer" : ""}
        `;
    }
  }, [type, disabled, onTap]);

  // Path preview overlay styles
  const pathPreviewStyles = isPathPreview
    ? `
        before:absolute before:inset-2
        before:rounded-lg before:bg-pixel/20
        before:border-2 before:border-dashed before:border-pixel/50
        ${isCurrentStep ? "before:animate-pulse before:bg-pixel/30" : ""}
      `
    : "";

  // Get collectible display
  const getCollectibleIcon = () => {
    if (!collectible || collectible.collected) return null;

    const icons: Record<typeof collectible.type, { emoji: string; style: string }> = {
      star: {
        emoji: "⭐",
        style: "text-2xl md:text-3xl animate-float drop-shadow-[0_2px_4px_rgba(255,193,7,0.4)]",
      },
      gem: {
        emoji: "💎",
        style: "text-2xl md:text-3xl animate-float drop-shadow-[0_2px_4px_rgba(0,191,255,0.4)]",
      },
      gear: {
        emoji: "⚙️",
        style: "text-2xl md:text-3xl animate-spin-slow drop-shadow-[0_2px_4px_rgba(255,140,66,0.4)]",
      },
      chip: {
        emoji: "🔲",
        style: "text-2xl md:text-3xl animate-pulse drop-shadow-[0_2px_4px_rgba(0,212,255,0.4)]",
      },
    };

    const icon = icons[collectible.type];
    return (
      <span className={icon.style} role="img" aria-label={collectible.type}>
        {icon.emoji}
      </span>
    );
  };

  // Get cell type indicator
  const getCellIndicator = () => {
    switch (type) {
      case "start":
        return (
          <div className="flex flex-col items-center gap-0.5">
            <span className="text-xl md:text-2xl" role="img" aria-label="start">
              🚀
            </span>
            <span className="text-body-md font-display font-bold text-white/90 uppercase tracking-wider">
              Start
            </span>
          </div>
        );

      case "goal":
        return (
          <div className="flex flex-col items-center gap-0.5">
            <span className="text-xl md:text-2xl animate-bounce" role="img" aria-label="goal">
              🏁
            </span>
            <span className="text-body-md font-display font-bold text-pixel-accent uppercase tracking-wider">
              Goal
            </span>
          </div>
        );

      case "obstacle":
        return (
          <div className="relative">
            {/* Gear obstacle decoration */}
            <svg
              className="w-8 h-8 md:w-10 md:h-10 text-neutral-600 drop-shadow-sm"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M12 15.5A3.5 3.5 0 0 1 8.5 12 3.5 3.5 0 0 1 12 8.5a3.5 3.5 0 0 1 3.5 3.5 3.5 3.5 0 0 1-3.5 3.5m7.43-2.53c.04-.32.07-.64.07-.97 0-.33-.03-.66-.07-1l2.11-1.63c.19-.15.24-.42.12-.64l-2-3.46c-.12-.22-.39-.31-.61-.22l-2.49 1c-.52-.39-1.06-.73-1.69-.98l-.37-2.65A.506.506 0 0 0 14 2h-4c-.25 0-.46.18-.5.42l-.37 2.65c-.63.25-1.17.59-1.69.98l-2.49-1c-.22-.09-.49 0-.61.22l-2 3.46c-.13.22-.07.49.12.64L4.57 11c-.04.34-.07.67-.07 1 0 .33.03.65.07.97l-2.11 1.66c-.19.15-.25.42-.12.64l2 3.46c.12.22.39.3.61.22l2.49-1.01c.52.4 1.06.74 1.69.99l.37 2.65c.04.24.25.42.5.42h4c.25 0 .46-.18.5-.42l.37-2.65c.63-.26 1.17-.59 1.69-.99l2.49 1.01c.22.08.49 0 .61-.22l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.66Z" />
            </svg>
            {/* X mark overlay */}
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-red-500/60 text-lg font-bold">✕</span>
            </div>
          </div>
        );

      case "collectible":
        return getCollectibleIcon();

      default:
        return null;
    }
  };

  return (
    <button
      type="button"
      onClick={onTap}
      disabled={disabled || !onTap}
      className={`${cellStyles} ${pathPreviewStyles}`}
      aria-label={`Maze cell at row ${position[0] + 1}, column ${position[1] + 1}, type: ${type}`}
    >
      {/* Cell content */}
      {getCellIndicator()}

      {/* Corner decorations for empty cells - subtle gear/circuit pattern */}
      {type === "empty" && !isPathPreview && (
        <>
          <div className="absolute top-1 left-1 w-1.5 h-1.5 rounded-full bg-neutral-200" />
          <div className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-neutral-200" />
          <div className="absolute bottom-1 left-1 w-1.5 h-1.5 rounded-full bg-neutral-200" />
          <div className="absolute bottom-1 right-1 w-1.5 h-1.5 rounded-full bg-neutral-200" />
        </>
      )}

      {/* Current step glow effect */}
      {isCurrentStep && (
        <div
          className="
            absolute inset-0 rounded-xl
            bg-pixel/20 animate-ping
            pointer-events-none
          "
          style={{ animationDuration: "1s" }}
        />
      )}
    </button>
  );
}

export default MazeCell;
